<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, toRef, watch } from 'vue';
import { useOfferFiltersStore } from '@/stores/offerFilters.ts';
import { useProductListFiltersStore } from '@/stores/productListFilters.ts';
import { SessionStorageService } from '@/services/storage/sessionStorageService.ts';
import { usePlayerAvailableValuesStore } from '@/stores/playerAvailableValues.ts';
import { useOfferInitializer } from '@/composables/offers/useOfferInitializer.ts';
import { useModalStore, ModalType } from '@/stores/modal.ts';
import { loadIbeSearchDefaults } from '@/composables/filters/useSearchDefaults.ts';
import {
  resolveDurationFlex,
  resolveOfferPageSize,
} from '@/utils/searchDefaults/searchDefaultsResolver.ts';
import SearchBar from '@/components/ui/SearchBar.vue';
import DateRangeModal from '@/components/shared/DateRangeModal.vue';
import MoreOptionsModal from '@/components/shared/MoreOptionsModal.vue';
import BaseAlert from '@/components/ui/BaseAlert.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import ProductOffer from '@/components/ui/ProductOffer.vue';
import ProductOfferSkeleton from '@/components/ui/ProductOfferSkeleton.vue';
import OfferEmptyState from '@/components/offer/OfferEmptyState.vue';
import ResultListTeaser from '@/components/productList/ResultListTeaser.vue';
import { useResultListTeaserStore } from '@/stores/resultListTeaser.ts';
import { $t } from '@/composables/translations/useTranslate.ts';
import { serializePaxString } from '@/utils/parsing/paxParser.ts';
import {
  applyClassificationsToOccupancy,
  canonicalizeRoomOccupancy,
} from '@/utils/pax/canonicalOrder.ts';
import type { CalendarRestriction, RoomOccupancy } from '@/types/SearchBar.ts';
import { useHotelAvailability } from '@/composables/offers/useHotelAvailability.ts';
import {
  arrivalDaySet,
  isPresetOffered,
  largestOfferedWithin,
  minStay,
} from '@/utils/calendar/hotelAvailability.ts';
import { formatDateLocal } from '@/utils/formatting/dateFormatter.ts';
import {
  formatDateRange,
  compareCombinationsFixedFirst,
  type RoomCombination,
  type MappedOffer,
} from '@/services/mappers/offerMapper.ts';
import { formatPrice } from '@/utils/formatting/currencyFormatter';
import { formatOccupancySummary } from '@/utils/formatting/formatOccupancySummary.ts';
import { getCodeMeta, isAvailableCode, type StatusType } from '@/constants/davinci/returnCodes';
import { buildCheckoutUrl, shouldRedirectToCheckout } from '@/utils/checkout/checkoutUtils.ts';
import { saveOfferPrices } from '@/utils/checkout/offerPriceStorage.ts';
import { useDebugMode } from '@/composables/debug/useDebugMode';
import { useAnalytics } from '@/composables/useAnalytics';

const offerStore = useOfferFiltersStore();
const productListFiltersStore = useProductListFiltersStore();
const availableValuesStore = usePlayerAvailableValuesStore();
const modalStore = useModalStore();
const teaserStore = useResultListTeaserStore();
const { initializeFilters } = useOfferInitializer();
const { isDebugMode } = useDebugMode();
const analytics = useAnalytics();
const offerPageSize = resolveOfferPageSize(loadIbeSearchDefaults());

const visibleCount = ref(offerPageSize);

const searchBarRef = ref<InstanceType<typeof SearchBar> | null>(null);
const containerRef = ref<HTMLElement | null>(null);
const attentionPulseStarted = ref(false);

let intersectionObserver: IntersectionObserver | null = null;

onMounted(async () => {
  await availableValuesStore.fetchAvailableValues();
  await initializeFilters();

  // Load the Pimcore contact teaser (reused from the product list) so it can be shown
  // under the empty/error states below; the store no-ops if already loaded.
  teaserStore.fetchTeaser();

  // Start pulsing the search-bar date/companions buttons when the user scrolls to the
  // Vue app; the SearchBar keeps pulsing until the user interacts with dates or pax
  if (!offerStore.hasConfirmedSearchParams && !attentionPulseStarted.value && containerRef.value) {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!attentionPulseStarted.value && entry.isIntersecting) {
          if (offerStore.hasConfirmedSearchParams) {
            intersectionObserver?.disconnect();
            return;
          }

          const viewportHeight = window.innerHeight;
          const visibleHeight = entry.intersectionRect.height;
          const viewportCoverage = visibleHeight / viewportHeight;

          if (viewportCoverage >= 0.2) {
            searchBarRef.value?.pulseAttention();
            attentionPulseStarted.value = true;
            intersectionObserver?.disconnect();
          }
        }
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
    );
    intersectionObserver.observe(containerRef.value);
  }

  if (offerStore.hasProductCode) {
    await offerStore.fetchOffers();
    await offerStore.fetchNbcData();
    analytics.trackPdpViewed(
      offerStore.productCode,
      offerStore.nbcData?.name ?? offerStore.productCode
    );
  }
});

onUnmounted(() => {
  intersectionObserver?.disconnect();
});

const alertMessage = computed(() =>
  $t(
    'ibe.offer.complete-params-prompt',
    'Please complete your search parameters to view all available offers'
  )
);

const canRetry = computed(
  () => offerStore.errorType === 'network' || offerStore.errorType === 'timeout'
);

async function handleRetry() {
  await offerStore.retryFetch();
}

const roomGroupsData = computed(() => offerStore.roomCombinationGroups);

const paxSummary = computed(() => formatOccupancySummary(offerStore.roomOccupancy));

type DisplayAvailability = 'available' | 'on-request' | 'unavailable' | 'unknown';

function applyRequestBookingOverride<T extends DisplayAvailability | undefined>(
  availability: T,
  products: MappedOffer[]
): T | 'on-request' {
  if (availability === 'unavailable') return availability;
  return products.some((p) => p.requestBooking) ? 'on-request' : availability;
}

/**
 * Get status message, type and availability based on DaVinci availability check result
 * Works with both single-room and multi-room results (same structure)
 * Uses centralized registry for consistent messaging across all components.
 *
 * Returns availability as:
 * - 'available' for code 712 (fixed booking)
 * - 'on-request' for codes 707, 733, 734 (request booking) or 923 (confirmation needed)
 * - 'unavailable' for any non-available code
 * - undefined if no check result exists yet
 */
function getStatusFromCheck(result: { code: number; message: string } | undefined): {
  statusMessage: string | undefined;
  statusType: StatusType | undefined;
  availability: 'available' | 'on-request' | 'unavailable' | undefined;
} {
  if (!result) {
    return { statusMessage: undefined, statusType: undefined, availability: undefined };
  }

  const { code, message } = result;
  const meta = getCodeMeta(code);

  if (meta) {
    const statusMessage = $t(meta.translationKey, meta.fallbackMessage);
    const statusType = meta.statusType;
    const availability = meta.flowType === 'fixed' ? 'available' : 'on-request';

    return { statusMessage, statusType, availability };
  }

  if (!isAvailableCode(code)) {
    return {
      statusMessage: isDebugMode.value
        ? message || $t('ibe.davinci.not-available', 'This offer is currently not available')
        : $t(
            'ibe.availability.generic-error',
            'We apologize, but this room type is already fully booked for the selected dates. Please select a different room or a different period.'
          ),
      statusType: 'error' as const,
      availability: 'unavailable' as const,
    };
  }

  return { statusMessage: undefined, statusType: undefined, availability: undefined };
}

/**
 * Included board shown in the card heading, only for a group with a single offer.
 * A group with several board options lists them in its expandable meal options,
 * so the heading must not single out the cheapest one.
 */
function headingBoardName(room: (typeof displayedRoomGroups.value)[number]): string | undefined {
  return room.otherCombinations.length === 0 ? room.cheapestCombination.boardName : undefined;
}

/**
 * Card title of a single-room group: room name, followed by the included board when
 * the group has a single offer ("Doppelzimmer, Halbpension"). Multi-room cards have
 * no title; their board goes after each per-room heading instead.
 */
function singleRoomTitle(room: (typeof displayedRoomGroups.value)[number]): string {
  if (room.isMultiRoom) return '';

  const roomName = room.roomDetails?.[0]?.nbc?.roomTypeName || room.roomDescriptions[0];

  return [roomName, headingBoardName(room)].filter(Boolean).join(', ');
}

/**
 * Generate unique combination ID for availability tracking
 */
function generateCombinationId(combination: RoomCombination): string {
  return combination.products.map((p) => p.offerId).join('-');
}

/**
 * Build availability data for display
 */
const roomGroupsWithAvailability = computed(() => {
  const groups = roomGroupsData.value;
  const multiRoomChecks = offerStore.multiRoomAvailabilityChecks;

  return groups.map((group) => {
    // All combinations ordered for display: fixed-bookable first, then by price
    const allCombinations = [group.cheapestCombination, ...group.otherCombinations].sort(
      compareCombinationsFixedFirst
    );

    // Check availability for cheapest combination
    const combinationId = generateCombinationId(group.cheapestCombination);
    const cheapestResult = multiRoomChecks.get(combinationId);
    const isCheckingCheapest = offerStore.isCheckingCombinationAvailability(combinationId);

    const {
      statusMessage,
      statusType,
      availability: derivedAvailability,
    } = getStatusFromCheck(cheapestResult);

    // Build meal options from all combinations and track if any is unavailable
    let anyUnavailable = false;
    let firstUnavailableStatus: {
      message: string | undefined;
      type: 'info' | 'success' | 'warning' | 'error' | undefined;
    } | null = null;

    const mealOptions = allCombinations.map((combo) => {
      const comboId = generateCombinationId(combo);
      const result = multiRoomChecks.get(comboId);
      const isChecking = offerStore.isCheckingCombinationAvailability(comboId);

      const { statusMessage: optionStatusMessage, statusType: optionStatusType } =
        getStatusFromCheck(result);

      // If any combination is unavailable, mark the whole group and capture first unavailable status
      if (result && !result.available) {
        if (!firstUnavailableStatus) {
          firstUnavailableStatus = {
            message: optionStatusMessage,
            type: optionStatusType,
          };
        }
        anyUnavailable = true;
      }

      const firstProduct = combo.products[0];

      return {
        id: generateCombinationId(combo),
        title: combo.boardName,
        price: formatPrice(combo.totalPrice, true),
        pricePrefix: 'ab',
        hasVamosBadge: false,
        actionText: $t('ibe.aux.select-cta', 'Select now'),
        discount: combo.discount,
        discountReason: combo.discountReason,
        oldPrice: combo.oldPrice,
        offerId: firstProduct.offerId,
        isCheckingAvailability: isChecking,
        statusMessage: optionStatusMessage,
        statusType: optionStatusType,
      };
    });

    // Determine final group availability: unavailable if any option is unavailable
    const baseAvailability: DisplayAvailability = anyUnavailable
      ? 'unavailable'
      : (derivedAvailability ?? group.availability);
    const finalAvailability = applyRequestBookingOverride(
      baseAvailability,
      group.cheapestCombination.products
    );

    const unavailableStatus = firstUnavailableStatus as {
      message: string | undefined;
      type: 'info' | 'success' | 'warning' | 'error' | undefined;
    } | null;
    const finalStatusMessage =
      anyUnavailable && unavailableStatus ? unavailableStatus.message : statusMessage;
    const finalStatusType =
      anyUnavailable && unavailableStatus ? unavailableStatus.type : statusType;

    return {
      ...group,
      availability: finalAvailability,
      statusMessage: finalStatusMessage,
      statusType: finalStatusType,
      mealOptions,
      isMultiRoom: group.isMultiRoom,
      isCheckingAvailability: isCheckingCheapest,
    };
  });
});

const allRoomsUnavailable = computed(() => {
  const rooms = roomGroupsWithAvailability.value;
  if (rooms.length === 0) return false;
  return rooms.every((room) => room.availability === 'unavailable');
});

const displayedRoomGroups = computed(() =>
  offerStore.hasConfirmedSearchParams
    ? roomGroupsWithAvailability.value.slice(0, visibleCount.value)
    : roomGroupsWithAvailability.value.slice(0, offerPageSize)
);

const hasMoreOffers = computed(
  () => showFullList.value && roomGroupsWithAvailability.value.length > visibleCount.value
);

function showMoreOffers() {
  visibleCount.value += offerPageSize;
}

watch(
  () => offerStore.roomCombinationGroups,
  () => {
    visibleCount.value = offerPageSize;
  }
);

const showPreviewList = computed(
  () =>
    !offerStore.hasConfirmedSearchParams &&
    offerStore.hasAllRequiredParams &&
    displayedRoomGroups.value.length > 0
);

const showFullList = computed(
  () =>
    offerStore.hasConfirmedSearchParams &&
    offerStore.hasAllRequiredParams &&
    displayedRoomGroups.value.length > 0
);

const showOfferList = computed(() => showPreviewList.value || showFullList.value);

/**
 * Check availability and proceed to checkout
 */
async function handleOfferClick(combination: RoomCombination) {
  if (!offerStore.apiParams) {
    console.warn('Cannot check availability: missing API params');
    return;
  }

  const combinationId = generateCombinationId(combination);

  await offerStore.checkCombinationAvailability(combination, offerStore.productCode);

  const availabilityResult = offerStore.getCombinationAvailabilityResult(combinationId);

  if (availabilityResult) {
    if (availabilityResult.available) {
      analytics.trackBookingCheckSuccess(
        availabilityResult.code,
        availabilityResult.sessionId ?? null,
        'pdp'
      );
    } else {
      analytics.trackBookingCheckFail(
        availabilityResult.code,
        availabilityResult.message || 'Unavailable',
        availabilityResult.sessionId ?? null,
        'pdp'
      );
    }
  }

  if (availabilityResult && shouldRedirectToCheckout(availabilityResult.code)) {
    if (!offerStore.roomOccupancy) {
      console.error('[OfferPage] Cannot redirect to checkout: roomOccupancy is missing');
      return;
    }

    // Establish canonical order ONCE at the offer→checkout boundary
    const classified = applyClassificationsToOccupancy(
      offerStore.roomOccupancy,
      combination.allPassengers
    );
    const { orderedOccupancy, orderedCombination } = canonicalizeRoomOccupancy(
      classified,
      combination
    );

    // Save prices before redirect for deviation detection in checkout.
    const paxString = serializePaxString(orderedOccupancy);
    saveOfferPrices(orderedCombination, offerStore.productCode, paxString || '');

    // The checkout URL carries the ANNOTATED pax (classification survives the redirect).
    const checkoutUrl = buildCheckoutUrl(
      orderedCombination,
      offerStore.productCode,
      orderedOccupancy
    );

    window.location.href = checkoutUrl;
  }
}

const handleRoomOccupancyChange = (newOccupancy: RoomOccupancy) => {
  offerStore.setRoomOccupancy(newOccupancy);
};

const handleSearchClick = async () => {
  await offerStore.fetchOffers();
};

function resolveSrpUrl(): string {
  const stored = SessionStorageService.readSrpUrl();
  if (stored) return stored;

  const referrer = document.referrer;
  if (referrer) {
    try {
      const referrerUrl = new URL(referrer);
      if (
        referrerUrl.origin === window.location.origin &&
        referrerUrl.pathname !== window.location.pathname
      ) {
        return referrer;
      }
    } catch {
      // Ignore malformed referrer
    }
  }

  return window.location.pathname;
}

function handleBackToSearch() {
  const url = new URL(resolveSrpUrl(), window.location.origin);

  const params = offerStore.apiParams;
  if (params) {
    url.searchParams.set('date', params.startDate);
    url.searchParams.set('todate', params.endDate);
    url.searchParams.set('days', params.duration.toString());
  }

  // The stored search URL predates this visit, so its dmode is whatever the user
  // had when they left. Overwrite it either way: carrying a stale `exact` back is
  // as wrong as dropping a freshly chosen one.
  if (offerStore.durationMode === 'exact') {
    url.searchParams.set('dmode', 'exact');
  } else {
    url.searchParams.delete('dmode');
  }

  if (offerStore.roomOccupancy) {
    const paxString = serializePaxString(offerStore.roomOccupancy);
    if (paxString) {
      url.searchParams.set('pax', paxString);
    }
  }

  window.location.href = url.toString();
}

function handleStartNewSearch() {
  const srpPath = new URL(resolveSrpUrl(), window.location.origin).pathname;
  productListFiltersStore.clearPersistedFilters();
  SessionStorageService.clear();
  window.location.href = srpPath;
}

watch(
  () => offerStore.hasAllRequiredParams,
  async (isComplete, wasComplete) => {
    if (isComplete && !wasComplete) {
      await offerStore.fetchOffers();
    }
  }
);

// `durationMode` is watched alongside the request params because it is not part
// of them: it decides whether the zero-result retry may widen the length, so
// flipping it has to re-run the search even though the request is unchanged.
watch(
  [() => offerStore.apiParams, () => offerStore.durationMode],
  async ([newParams]) => {
    if (newParams && offerStore.hasAllRequiredParams) {
      await offerStore.fetchOffers();
    }
  },
  { deep: true }
);

const hotelAvailability = useHotelAvailability(
  toRef(offerStore, 'productCode'),
  toRef(offerStore, 'roomOccupancy')
);

const durationFlex = resolveDurationFlex(loadIbeSearchDefaults());

const canRestrictCalendar = computed(
  () => offerStore.hasRoomOccupancy && offerStore.hasProductCode
);

// Fetch available values and the hotel's dates when the date modal opens
watch(
  () => modalStore.isModalOpen(ModalType.DATE_PICKER),
  async (isOpen) => {
    if (isOpen) {
      if (canRestrictCalendar.value) {
        void hotelAvailability.load();
      }
      if (availableValuesStore.isStale || !availableValuesStore.hasData) {
        try {
          await availableValuesStore.fetchAvailableValues();
        } catch (error) {
          console.error('Failed to fetch available values:', error);
        }
      }
    }
  }
);

const dateConstraints = computed(() => ({
  min: availableValuesStore.dateRangeForInput.min || undefined,
  max: availableValuesStore.dateRangeForInput.max || undefined,
}));

/** Arrival bounds as the picker applies them: its defaults are today and one year ahead. */
function arrivalBounds(): { from: string; to: string } {
  const today = new Date();
  const nextYear = new Date();
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  return {
    from: dateConstraints.value.min ?? formatDateLocal(today),
    to: dateConstraints.value.max ?? formatDateLocal(nextYear),
  };
}

/**
 * The hotel's dates for the current occupancy, handed to the date modal.
 * `undefined` (no occupancy, no product, nothing loaded yet) keeps the
 * calendar exactly as on the result list.
 */
const calendarRestriction = computed<CalendarRestriction | undefined>(() => {
  const arrivalStatus = hotelAvailability.arrivalStatus.value;
  const durationsStatus = hotelAvailability.durationsStatus.value;
  if (!canRestrictCalendar.value || arrivalStatus === 'idle' || durationsStatus === 'idle') {
    return undefined;
  }

  const checkInDates = hotelAvailability.checkInDates.value;
  const { from, to } = arrivalBounds();
  const arrivalDays = checkInDates ? arrivalDaySet(checkInDates, from, to) : new Set<string>();
  const offered = hotelAvailability.offeredDurations.value;
  const flex = durationFlex;

  return {
    arrivalStatus: arrivalStatus === 'ready' && arrivalDays.size === 0 ? 'empty' : arrivalStatus,
    durationsStatus,
    arrivalDays,
    minOffered: minStay(offered) ?? 1,
    durationsFor: hotelAvailability.durationsFor,
    markNotArrival: hotelAvailability.markNotArrival,
    isPresetOffered: (preset) => isPresetOffered(preset, offered, flex),
    fallbackDuration: (nights) => largestOfferedWithin(offered, nights),
  };
});
</script>

<template>
  <div ref="containerRef" class="mb-28">
    <div v-if="!offerStore.hasConfirmedSearchParams" class="wrapper mb-6">
      <BaseAlert type="success" icon="icon-info" :message="alertMessage" />
    </div>

    <div class="lg:mb-5 mb-4">
      <SearchBar
        ref="searchBarRef"
        hide-destination
        auto-detect-initial-values
        :date-range="offerStore.dateRange"
        :room-occupancy="offerStore.roomOccupancy"
        :loading="offerStore.loading"
        @room-occupancy-change="handleRoomOccupancyChange"
        @search-click="handleSearchClick"
      />
    </div>

    <div v-if="offerStore.loading" class="wrapper" aria-live="polite" aria-busy="true">
      <span class="sr-only">{{
        $t('ibe.common.loading-offers', 'Loading offers, please wait...')
      }}</span>
      <div class="space-y-2">
        <ProductOfferSkeleton v-for="i in 3" :key="`skeleton-${i}`" />
      </div>
    </div>

    <div v-else-if="offerStore.error" class="wrapper mb-6">
      <BaseAlert
        type="error"
        :message="`${$t('ibe.offer.error-loading', 'Error loading offers:')} ${offerStore.errorMessage}`"
        :action-button="
          canRetry
            ? {
                text: $t('ibe.common.retry', 'Retry'),
                onClick: handleRetry,
              }
            : undefined
        "
      />
      <ResultListTeaser class="mt-6" />
    </div>

    <div v-else-if="allRoomsUnavailable && showFullList" class="wrapper">
      <OfferEmptyState
        type="all-unavailable"
        @back-to-search="handleBackToSearch"
        @start-new-search="handleStartNewSearch"
      />
      <ResultListTeaser class="mt-6" />
    </div>

    <div
      v-else-if="showOfferList"
      class="wrapper max-w-[1070px] flex flex-col gap-2"
      aria-live="polite"
    >
      <span class="sr-only">{{
        $t('ibe.offer.results-count', 'Found {count} room options', {
          count: displayedRoomGroups.length,
        })
      }}</span>
      <ProductOffer
        v-for="room in displayedRoomGroups"
        :key="room.groupKey"
        :date-range="formatDateRange(room.startDate, room.endDate)"
        :pax-summary="paxSummary"
        :title="singleRoomTitle(room)"
        description=""
        :availability="room.availability === 'unknown' ? 'available' : room.availability"
        :price="formatPrice(room.cheapestCombination.totalPrice, true)"
        price-prefix="ab"
        :discount="room.cheapestCombination.discount"
        :discount-reason="room.cheapestCombination.discountReason"
        :old-price="room.cheapestCombination.oldPrice"
        :expandable="room.otherCombinations.length > 0"
        :is-checking-availability="room.isCheckingAvailability"
        :status-message="room.statusMessage"
        :status-type="room.statusType"
        :meal-options="room.mealOptions"
        :room-images="!room.isMultiRoom ? room.roomDetails?.[0]?.nbc?.images : undefined"
        :html-description="!room.isMultiRoom ? room.roomDetails?.[0]?.nbc?.description : undefined"
        :room-details="room.roomDetails"
        :board-name="room.isMultiRoom ? headingBoardName(room) : undefined"
        @cta-click="handleOfferClick(room.cheapestCombination)"
        @meal-option-click="
          (offerId) => {
            const allCombos = [room.cheapestCombination, ...room.otherCombinations];
            const combo = allCombos.find((c) => c.products[0].offerId === offerId);
            if (combo) handleOfferClick(combo);
          }
        "
      />
      <BaseButton
        v-if="hasMoreOffers"
        variant="secondary"
        class="mt-4 self-center"
        @click="showMoreOffers"
      >
        {{ $t('ibe.offer.show-more', 'Show more') }}
      </BaseButton>
      <BaseAlert
        v-if="showPreviewList"
        class="mt-4"
        type="success"
        icon="icon-info"
        :message="alertMessage"
      />
    </div>

    <div v-else class="wrapper">
      <OfferEmptyState type="no-results" />
      <ResultListTeaser class="mt-6" />
    </div>

    <DateRangeModal
      :date-range="offerStore.dateRange"
      :duration="offerStore.duration"
      :duration-mode="offerStore.durationMode"
      :min-date="dateConstraints.min"
      :max-date="dateConstraints.max"
      :restriction="calendarRestriction"
      @update:date-range="offerStore.setDateRange"
      @update:duration="offerStore.setDuration"
      @update:duration-mode="offerStore.setDurationMode"
    />

    <MoreOptionsModal />
  </div>
</template>
